import { defer, firstValueFrom, forkJoin, from, Observable, of, zip } from "rxjs";
import { catchError, map, mergeMap, switchMap, switchMapTo, take } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Inspection } from "../_objects/inspection";
import { InspectionImage, InspectionImageListItem } from "../_objects/inspection-image";
import { AnnotationImageDetail } from "../_objects/inspection-image-annotation";
import { InspectionReport, InspectionReportList, InspectionReportListItem, PastReportVersion } from "../_objects/inspection-report";
import { Mark } from "../_objects/mark";
import { ImageMetadataForOffline } from "../_objects/metadata/image-metadata-for-offline";
import { TenantDetail } from "../_objects/offline-management";
import { Subject, SubjectAttributes, SubjectDetail } from "../_objects/subject";
import { SubjectLocation, SubjectModel, SubjectModelDetail, SubjectModelLocations } from "../_objects/subject-model";
import { Tags } from "../_objects/tags";
import { ConfigurationItem } from "../_objects/tenant-config";
import { Title } from "../_objects/title";
import { User, UserDetail } from "../_objects/user";
import { ProgressDialogComponent } from "../_offline/component/dialog/progress-dialog/progress-dialog.component";
import { DATA_SYNC_STEP } from "../_offline/electron.constants";
import { CurrentUserSchema } from "../_offline/models/current-user.schema";
import { IndividualReportTemplateSchema } from "../_offline/models/individual-report-template.schema";
import { IndividualReportSchema } from "../_offline/models/individual-report.schema";
import { InspectionSchema } from "../_offline/models/inspection.schema";
import { InspectionImageSchema } from "../_offline/models/inspectionImage.schema";
import { MarkSchema } from "../_offline/models/mark.schema";
import { ImagePath, IndividualReportTemplate } from "../_offline/models/report-preview/report-template";
import { SubjectSchema } from "../_offline/models/subject.schema";
import { SubjectModelLocationsSchema } from "../_offline/models/subjectModel-locations.schema";
import { SubjectModelSchema } from "../_offline/models/subjectModel.schema";
import { TagsSchema } from "../_offline/models/tags.schema";
import { TenantDetailSchema } from "../_offline/models/tenant-detail.schema";
import { TenantConfigSchema } from "../_offline/models/tenantConfiguration.schema";
import { ConnectorSchema } from "../_offline/models/connector-schema";
import { TitleSchema } from "../_offline/models/title.schema";
import { UploadDataType, UploadManagementSchema } from "../_offline/models/upload-management.schema";
import { UserSchema } from "../_offline/models/user.schema";
import { FEATURE_FLAGS, INSPECTION_REPORT_STATUS_CODE, MEASUREMENT_IMAGE_TYPE } from "../app.constants";
import { UtilsService } from "./_utils.service";
import { CommonSnackBarService } from "./common-snack-bar.service";
import { CurrentUserDbService } from "./database/current-user-db.service";
import { IndividualReportDbService } from "./database/individual-report-db.service";
import { IndividualReportTemplateDbService } from "./database/individual-report-template-db.service";
import { InspectionDbService } from "./database/inspection-db.service";
import { InspectionImageDbService } from "./database/inspection-image-db.service";
import { MarkDbService } from "./database/mark-db.service";
import { SubjectDbService } from "./database/subject-db.service";
import { SubjectModelDbService } from "./database/subject-model-db.service";
import { SubjectModelLocationsDbService } from "./database/subject-model-locations-db.service";
import { TagsDbService } from "./database/tags-db.service";
import { TenantConfigurationDbService } from "./database/tenant-configuration-db.service";
import { TenantDetailDbService } from "./database/tenant-detail-db.service";
import { TitleDbService } from "./database/title-db.service";
import { UploadManagementDbService } from "./database/upload-management-db.service";
import { UserDbService } from "./database/user-db.service";
import { FileSystemAccessService } from "./file-system-access.service";
import { MeUserService } from "./http-api/me-user.service";
import { NetworkStatusService } from "./network-status.service";
import { OfflineManagementService } from "./offline-management.service";
import { LoginUserOfflineService } from "./offline/login-user-offline.service";
import { StereoMeasurementImageOfflineService } from "./offline/stereo-measurement-offline.service";
import { AnnotationImageOnlineService } from "./online/annotation-image-online.service";
import { InspectionImageOnlineService } from "./online/inspection-image-online.service";
import { InspectionOnlineService } from "./online/inspection-online.service";
import { InspectionReportOnlineService } from "./online/inspection-report-online.service";
import { SpinnerService } from "./spinner.service";
import { SubjectAttributesSchema } from "../_offline/models/subject-attributes.schema";
import { SubjectAttributesDbService } from "./database/subject-attributes-db.service";
import { TranslateService } from "@ngx-translate/core";
import { ProgressDialogService } from "../_offline/component/dialog/progress-dialog/progress-dialog.service";
import { ConnectorDbService } from "./database/connector-db.service";
import { ConnectorStatus } from "../_objects/connector";
import { ConnectorOnlineService } from "./online/connector-online.service";
import { environment } from "../../environments/environment";
import { SubjectOnlineService } from "./online/subject-online.service";
import { AssetModuleSyncService } from "./sync/asset-module-sync.service";
import { FeatureFlagService } from "./feature-flag/feature-flag.service";

export interface DataSyncProgress {
  syncStep: number;
  totalDataCount: number;
  dataTitle?: string;
  descriptionList: ProgressBarDescription[];
}

export interface ProgressBarDescription {
  description?: string;
  syncType: DATA_SYNC_STEP;
  successCount?: number;
  totalCount: number;
}

@Injectable({
  providedIn: "root"
})
export class OfflineDataSyncService {
  private _isFirst = true;

  private get tenantId() {
    return this.loginUserService.tenantId;
  }

  private get userId() {
    return this.loginUserService.loginUser?.id;
  }

  public get isFirst() {
    return this._isFirst;
  }

  public set isFirst(value: boolean) {
    this._isFirst = value;
  }

  public async syncAssetModuleData(): Promise<void> {
    this.progressDialogService.updateTotalDataCount({
      totalDataCount: 1,
      syncStep: DATA_SYNC_STEP.DOWNLOAD_ASSET_MODULE_DATA,
      descriptionList: [
        {
          syncType: DATA_SYNC_STEP.DOWNLOAD_ASSET_MODULE_DATA,
          totalCount: 1
        }
      ]
    });

    try {
      await this.assetModuleSyncService.pullFromServer();
      this.progressDialogService.updateProgressStep(DATA_SYNC_STEP.DOWNLOAD_ASSET_MODULE_DATA);
    } catch (error) {
      this.snackBar.open(this.translate.instant("dataUploadDialog.errorOccured"));
    }
  }

  private folderIndex = 1;

  constructor(
    private annotationImageOnlineService: AnnotationImageOnlineService,
    private currentUserDbService: CurrentUserDbService,
    private dialog: MatDialog,
    private fileSystemAccessService: FileSystemAccessService,
    private individualReportDbService: IndividualReportDbService,
    private individualReportTemplateDbService: IndividualReportTemplateDbService,
    private inspectionImageDbService: InspectionImageDbService,
    private inspectionDbService: InspectionDbService,
    private inspectionImageOnlineService: InspectionImageOnlineService,
    private inspectionOnlineService: InspectionOnlineService,
    private loginUserService: LoginUserOfflineService,
    private markDbService: MarkDbService,
    private meUserService: MeUserService,
    private networkStatusService: NetworkStatusService,
    private offlineManagementService: OfflineManagementService,
    private oscSpinnerService: SpinnerService,
    private progressDialogService: ProgressDialogService,
    private reportOnlineService: InspectionReportOnlineService,
    private snackBar: CommonSnackBarService,
    private stereoMeasurementImageOfflineService: StereoMeasurementImageOfflineService,
    private subjectDbService: SubjectDbService,
    private subjectAttributesDbService: SubjectAttributesDbService,
    private subjectModelDbService: SubjectModelDbService,
    private subjectModelLocationsDbService: SubjectModelLocationsDbService,
    private subjectOnlineService: SubjectOnlineService,
    private tagsDbService: TagsDbService,
    private tenantConfigDbService: TenantConfigurationDbService,
    private tenantDetailDbService: TenantDetailDbService,
    private titleDbService: TitleDbService,
    private translate: TranslateService,
    private uploadManagementDbService: UploadManagementDbService,
    private userDbService: UserDbService,
    private utilsService: UtilsService,
    private connectorDbService: ConnectorDbService,
    private connectorOnlineService: ConnectorOnlineService,
    private assetModuleSyncService: AssetModuleSyncService,
    private featureFlagService: FeatureFlagService
  ) {}

  // マスターデータの取得・保存
  public async syncOnlineData(): Promise<boolean> {
    this.isFirst = false;

    const isAssetModuleEnabled = this.featureFlagService.isFlagEnabled(FEATURE_FLAGS.ASSET_MODULE)();

    const connectorRequests = this.getConnectors();

    this.oscSpinnerService.hide(true);

    /**
     * step1: download offline settings
     * step2: download image files from blob storage
     * step3: download checked out inspection data
     * step4: store asset module data (if enabled)
     */
    this.openProgressDialog(isAssetModuleEnabled ? 4 : 3);

    this.progressDialogService.updateTotalDataCount({
      totalDataCount: 1,
      syncStep: DATA_SYNC_STEP.DOWNLOAD_OFFLINE_SETTINGS,
      descriptionList: []
    });

    return zip(
      this.offlineManagementService.getOfflineData(),
      this.meUserService.getMe(),
      this.subjectModelDbService.getAll(false, false),
      this.individualReportTemplateDbService.getAll(null, false),
      this.inspectionOnlineService.getCheckedOutInspections(),
      forkJoin(connectorRequests)
    )
      .pipe(take(1))
      .toPromise()
      .then(async (values) => {
        this.progressDialogService.updateProgressStep(DATA_SYNC_STEP.DOWNLOAD_OFFLINE_SETTINGS);
        const offlineSettings = values[0];
        const userMe = values[1];
        const subjectModelSchemas = values[2];
        const individualReportSchemas = values[3];
        const checkoutInspections = values[4];
        const allConnectorStatuses = values[5] as (ConnectorStatus | null)[];

        // A SubjectModel associated with image data is one-to-one.
        // An individual report data associated with image data is one-to-many.
        const updateSubjectModels = offlineSettings.SubjectModels.filter((subjectModel) => {
          const target = subjectModelSchemas.find((schema) => schema.id === subjectModel.id);
          return !target || target.lastUpdateDate !== subjectModel.lastUpdateDate;
        });
        const updateIndividualReportTemplates = offlineSettings.IndividualReportTemplates.filter((template) => {
          const target = individualReportSchemas.find((schema) => schema.id === template.id);
          return !target || target.lastUpdateDate !== template.lastUpdateDate;
        });
        let individualReportImageCount = 0;
        updateIndividualReportTemplates.map((value) => (individualReportImageCount += value.imagePathList.length));
        this.progressDialogService.updateTotalDataCount({
          totalDataCount: updateSubjectModels.length + individualReportImageCount,
          syncStep: DATA_SYNC_STEP.DOWNLOAD_IMAGE_FILE,
          descriptionList: [
            {
              syncType: DATA_SYNC_STEP.DOWNLOAD_SUBJECT_MODEL_IMAGE_FILE,
              totalCount: updateSubjectModels.length
            },
            {
              syncType: DATA_SYNC_STEP.DOWNLOAD_REPORT_TEMPLATE_IMAGE_FILE,
              totalCount: individualReportImageCount
            }
          ]
        });
        return await Promise.all([
          this.saveSubjectModelDetail(updateSubjectModels)
            .then(() => this.saveSubject(offlineSettings.Subjects))
            .then(() => this.saveSubjectAttributes(offlineSettings.SubjectAttributes)),
          this.saveUser(offlineSettings.User),
          this.saveTitle(offlineSettings.Title),
          this.saveTags(offlineSettings.Tag),
          this.saveMark(offlineSettings.Mark),
          this.saveTenantConfig(offlineSettings.Configuration),
          this.saveIndividualReportTemplate(updateIndividualReportTemplates),
          this.saveCurrentUser(userMe),
          this.saveTenantDetail(offlineSettings.Tenant),
          this.saveSubjectModelLocations(offlineSettings.SubjectModelLocations),
          ...allConnectorStatuses.filter((status) => status !== null).map((status) => this.saveConnectorStatus(status))
        ])
          .then(() => {
            return this.checkOutInspectionDataForOffline(checkoutInspections);
          })
          .then(() => {
            if (!isAssetModuleEnabled) {
              this.dialog.closeAll();
            }
            return true;
          })
          .catch(() => {
            this.snackBar.open(this.translate.instant("dataUploadDialog.errorOccured"));
            this.dialog.closeAll();
            this.networkStatusService.switchOnlineMode();
            return false;
          });
      })
      .catch(() => {
        this.snackBar.open(this.translate.instant("dataUploadDialog.errorOccured"));
        this.dialog.closeAll();
        this.networkStatusService.switchOnlineMode();
        return false;
      });
  }

  private getConnectors(): Observable<ConnectorStatus | null>[] {
    return Object.values(environment.CONNECTORS).map((id) =>
      this.connectorOnlineService.getConnectorStatus(id).pipe(
        catchError((err) => {
          console.warn(`Could not fetch connector ${id}`, err);
          return of(null);
        })
      )
    );
  }

  private async checkOutInspectionData(inspection: Inspection) {
    const inspSchema = new InspectionSchema();
    inspSchema.setInspection(this.tenantId, inspection, false, this.userId);
    const ums = new UploadManagementSchema(inspSchema.id, this.tenantId, UploadDataType.Inspection, false, this.userId);

    const isAssetModuleEnabled = this.featureFlagService.isFlagEnabled(FEATURE_FLAGS.ASSET_MODULE)();

    return this.subjectOnlineService
      .getSubject(inspection.subjectId, inspection.locationVersion)
      .pipe(
        switchMap((subject) => {
          if (!isAssetModuleEnabled) {
            return of(subject);
          }
          // In asset_module mode, offline-settings returns Subjects: [] so subject_schema is never
          // populated during syncOnlineData. Seed the subject/model rows here so the FK constraints are respected
          const subjectModelSchema = new SubjectModelSchema(subject.subjectModel as SubjectModelDetail, this.tenantId);
          const subjectSchema = new SubjectSchema(this.tenantId, subject as SubjectDetail);
          subjectSchema.subjectAttributes = [];
          return this.subjectModelDbService.putOne(subjectModelSchema).pipe(
            switchMap(() => this.subjectDbService.putOne(subjectSchema)),
            map(() => subject)
          );
        }),
        switchMap((subject) =>
          forkJoin([
            this.inspectionDbService.addInspection(inspSchema),
            this.uploadManagementDbService.put(ums),
            this.inspectionImageOnlineService.getInspectionImages(inspection.id, null),
            this.reportOnlineService.getInspectionReportsByInspectionIds([inspection.id], 100, 1),
            of(subject)
          ])
        ),
        mergeMap((args) => {
          return this.getImagesAndReports(...args);
        }),
        mergeMap((args) => {
          return this.saveImagesAndReports(...args, inspection);
        }),
        catchError(() => {
          this.snackBar.open(this.translate.instant("offlineDataSyncService.checkoutInspectionError"));
          return of(false);
        })
      )
      .toPromise();
  }
  private getImagesAndReports(
    _inspectionSchema: InspectionSchema,
    _uploadManagementSchema: UploadManagementSchema,
    inspectionImageListItemArray: InspectionImageListItem[],
    inspectionReportList: InspectionReportList,
    subject: Subject
  ) {
    const reports = this.getRelatedReports(inspectionReportList.items);
    const { imageGetOb, annotationGetOb, metadataGetOb } = this.getImageObservables(inspectionImageListItemArray);

    return zip(imageGetOb, annotationGetOb, metadataGetOb, reports, of(inspectionReportList), of(subject.subjectModel));
  }

  private getRelatedReports(reports: InspectionReportListItem[]) {
    let reports$ = of([] as PastReportVersion[][]);
    if (reports.length > 0) {
      const reportVersionObs = reports.map((report) => this.reportOnlineService.getInspectionReportVersion(report.reportId));
      reports$ = zip(...reportVersionObs);
    }

    return reports$.pipe(
      switchMap((reportVersionList) => {
        if (reportVersionList.length < 1) {
          return of([] as InspectionReport[]);
        }

        let reportListObs: Observable<InspectionReport>[] = [];
        reportVersionList.forEach((reportVersions) => {
          const reportObs = reportVersions.map((report) => this.reportOnlineService.getInspectionReport(report.reportId, report.version));
          reportListObs = reportListObs.concat(reportObs);
        });

        return zip(...reportListObs);
      })
    );
  }

  private getImageObservables(inspectionImageListItemArray: InspectionImageListItem[]) {
    let imageGetOb = of([] as InspectionImage[]);
    let annotationGetOb = of([] as AnnotationImageDetail[]);
    let metadataGetOb: Observable<Array<ImageMetadataForOffline>> = of(new Array<ImageMetadataForOffline>());
    if (inspectionImageListItemArray.length > 0) {
      const imageObs = inspectionImageListItemArray.map((img) => this.inspectionImageOnlineService.getInspectionImage(img.inspectionId, img.id));
      const annotationObs = inspectionImageListItemArray.map((img) => {
        return img.hasModifiedImage || img.hasMeasurementImage
          ? this.annotationImageOnlineService.getImageAnnotations(img.inspectionId, img.id)
          : of(null as AnnotationImageDetail);
      });
      const metadataObs: Array<Observable<ImageMetadataForOffline>> = inspectionImageListItemArray.map((img) =>
        this.offlineManagementService.getInspectionImageMetadataForOffline(img.inspectionId, img.id)
      );

      imageGetOb = zip(...imageObs);
      annotationGetOb = zip(...annotationObs);
      metadataGetOb = zip(...metadataObs);
    }
    return { imageGetOb, annotationGetOb, metadataGetOb };
  }

  private async saveImagesAndReports(
    imageAn: InspectionImage[],
    annotation: AnnotationImageDetail[],
    metadata: ImageMetadataForOffline[],
    reports: InspectionReport[],
    inspectionReportList: InspectionReportList,
    subjectModel: SubjectModel,
    inspection: Inspection
  ) {
    await firstValueFrom(this.saveReports(reports, inspectionReportList.items));

    const locationFolderNames: string[] = [];
    subjectModel.locations.forEach((location) => {
      this.genLocationFolder(location, locationFolderNames);
    });
    this.folderIndex = 1;

    if (imageAn.length < 1 && imageAn.length < 1) {
      return;
    }

    const [
      imageFiles,
      annotationImageFiles,
      remeasurementImageFiles,
      thumbnailImageFiles,
      thumbnailAnnotationImageFiles,
      thumbnailRemeasurementImageFiles
    ] = await this.getImageFiles(imageAn);

    const imageSchemas = await this.getInspectionImageSchema(imageAn, subjectModel, inspection.title, annotation, metadata);

    return this.inspectionImageDbService
      .putList(imageSchemas)
      .pipe(
        switchMap(() => imageAn.map((img) => this.saveImageToDb(imageSchemas, img))),
        switchMap(() =>
          this.saveBase64InspImages(
            imageAn,
            imageFiles,
            annotationImageFiles,
            remeasurementImageFiles,
            thumbnailImageFiles,
            thumbnailAnnotationImageFiles,
            thumbnailRemeasurementImageFiles,
            imageSchemas
          )
        )
      )
      .toPromise();
  }

  private getInspectionImageSchema(
    imageAn: InspectionImage[],
    subjectModel: SubjectModel,
    inspectionFolderName: string,
    annotation: AnnotationImageDetail[],
    metadata: ImageMetadataForOffline[]
  ) {
    return Promise.all(
      imageAn.map(async (img, i) => {
        const termLocations = this.utilsService.getLocations(subjectModel, null);
        const folderName = this.getLocationFolder(termLocations, img.subjectLocationId);
        const localFilePath = this.genLocalFilePath(inspectionFolderName, folderName, img.fileName);
        return await this.genInspectionImageSchema(img, annotation[i], this.tenantId, localFilePath, metadata[i]);
      })
    );
  }

  private async saveImageToDb(imageSchemas, img) {
    const imageSchema = imageSchemas.find((schema) => schema.id === img.id);
    if (img.uploadAudioUrl) {
      let saveAudioFileOb = of(null as UploadManagementSchema);
      const audioFile: File | null = await this.utilsService.getFileFromUrl(img.uploadAudioUrl, imageSchema.fileName);
      saveAudioFileOb = this.imageAudioUpsert(imageSchema, audioFile);
      this.inspectionImageUpsert(imageSchema, audioFile).pipe(switchMapTo(saveAudioFileOb));
    }
    const schema = new UploadManagementSchema(img.id, this.tenantId, UploadDataType.InspectionImageInfo, false, this.userId, img.inspectionId);
    return this.uploadManagementDbService.put(schema);
  }

  private async checkOutInspectionDataForOffline(inspections: Inspection[]) {
    this.progressDialogService.updateTotalDataCount({
      totalDataCount: inspections.length,
      syncStep: DATA_SYNC_STEP.DOWNLOAD_CHECKED_OUT_INSPECTIONS,
      descriptionList: [
        {
          syncType: DATA_SYNC_STEP.DOWNLOAD_CHECKED_OUT_INSPECTIONS,
          totalCount: inspections.length
        }
      ]
    });

    const myPromises = inspections.map(async (inspection) => {
      this.progressDialogService.updateProgressStep(DATA_SYNC_STEP.DOWNLOAD_CHECKED_OUT_INSPECTIONS);
      const result = await this.checkOutInspectionData(inspection);
      return result;
    });
    return await Promise.all(myPromises);
  }

  private imageAudioUpsert(imageSchema: InspectionImageSchema, file: File) {
    const savePath = imageSchema.audioFilePath.split("/").join("\\");
    const imageFileManage = new UploadManagementSchema(
      imageSchema.id,
      this.tenantId,
      UploadDataType.InspectionMeasurementImageAudioFile,
      true,
      this.userId,
      imageSchema.inspectionId
    );
    return from(this.fileSystemAccessService.saveFile(file, savePath)).pipe(switchMapTo(this.uploadManagementDbService.put(imageFileManage)));
  }

  private inspectionImageUpsert(imageSchema: InspectionImageSchema, file: File) {
    const filePath = this.genFilePath(imageSchema.localFilePath);
    const imageFileManage = new UploadManagementSchema(
      imageSchema.id,
      this.tenantId,
      UploadDataType.InspectionImageFile,
      true,
      this.userId,
      imageSchema.inspectionId
    );
    return from(this.fileSystemAccessService.saveFile(file, filePath)).pipe(switchMapTo(this.uploadManagementDbService.put(imageFileManage)));
  }

  private genFilePath(filePath: string) {
    const filePathParts = filePath.split("/");
    const pathSeparator = this.fileSystemAccessService.getSystemPathSeparator();
    filePathParts[filePathParts.length - 1] = filePathParts[filePathParts.length - 1];
    return filePathParts.join(pathSeparator);
  }

  public deleteUploadedOfflineData() {
    return this.uploadManagementDbService
      .getAll(this.tenantId, this.userId)
      .toPromise()
      .then((values) => {
        const uploadedInspIds = values
          .filter((data) => data.type === UploadDataType.Inspection)
          .filter((data) => data.isUploaded || (data.isOfflineData && data.isDeleted))
          .map((data) => data.dataId);

        const updatedAllDataInspIds: string[] = [];
        uploadedInspIds.forEach((id) => {
          const childData = values.filter((data) => data.parentDataId === id);

          if (childData.length < 1) {
            updatedAllDataInspIds.push(id);
          } else {
            const isUploadedAllImageInfo = childData
              .filter((data) => data.type === UploadDataType.InspectionImageInfo)
              .every((data) => data.isUploaded || (data.isOfflineData && data.isDeleted));

            const isUploadedAllImageFile = childData
              .filter((data) => data.type === UploadDataType.InspectionImageFile)
              .every((data) => data.isUploaded || data.isDeleted);

            const isUploadedAllModifiedImageFile = childData
              .filter((data) => data.type === UploadDataType.InspectionModifiedImageFile)
              .every((data) => data.isUploaded || data.isDeleted);

            const isUploadedAllMeasurementImageFile: boolean = childData
              .filter((data) => data.type === UploadDataType.InspectionMeasurementImageFile)
              .every((data) => data.isUploaded || data.isDeleted);

            const isUploadedIndividualReport = childData
              .filter((data) => data.type === UploadDataType.IndividualReport)
              .every((data) => data.isUploaded || data.isDeleted);

            const isUploadedAllAudioFile = childData
              .filter((data) => data.type === UploadDataType.InspectionMeasurementImageAudioFile)
              .every((data) => data.isUploaded || data.isDeleted);

            if (
              isUploadedAllImageInfo &&
              isUploadedAllImageFile &&
              isUploadedAllModifiedImageFile &&
              isUploadedAllMeasurementImageFile &&
              isUploadedIndividualReport &&
              isUploadedAllAudioFile
            ) {
              updatedAllDataInspIds.push(id);
            }
          }
        });

        if (updatedAllDataInspIds.length < 1) {
          return of([]).toPromise();
        }

        const obs = updatedAllDataInspIds.map((id) => {
          return this.deleteInspectionOfflineData(id);
        });

        return zip(...obs).toPromise();
      })
      .then(() => true)
      .catch((error) => {
        console.log(error);
        return false;
      });
  }

  public deleteInspectionOfflineData(inspectionId: string, isForceDelete = false) {
    const pathSeparator = this.fileSystemAccessService.getSystemPathSeparator();
    return this.inspectionDbService.getInspection(inspectionId, false, null, isForceDelete).pipe(
      switchMap((inspection) => {
        const deleteFolder: string = `inspections${pathSeparator}${inspection.title}`;
        const measurementFolder: string = `inspections${pathSeparator}${inspectionId}`;

        return this.uploadManagementDbService.deletedRelatedDataId(inspectionId, false).pipe(
          switchMap((_) => {
            if (!_) {
              return;
            }
            // ローカルデータを削除
            return zip(
              this.inspectionDbService.deleteInspection(inspectionId, false),
              this.inspectionImageDbService
                .deleteALL(inspectionId, false)
                .pipe(
                  mergeMap((inspectionImageIds) =>
                    this.stereoMeasurementImageOfflineService.deleteMeasurementIntermediateInfos(inspectionId, inspectionImageIds)
                  )
                ),
              defer(() => this.fileSystemAccessService.deleteFolder(deleteFolder)),
              defer(() => this.fileSystemAccessService.deleteFolder(measurementFolder)),
              this.individualReportDbService.deleteALL(inspectionId, false)
            );
          })
        );
      })
    );
  }

  private async saveSubject(subjects: SubjectDetail[]) {
    const subjectsSchema = subjects.map((subject) => new SubjectSchema(this.tenantId, subject));
    await this.subjectDbService.putAll(subjectsSchema).toPromise().then();
  }

  private async saveSubjectModelDetail(subjectModels: SubjectModelDetail[]) {
    if (this.featureFlagService.isFlagEnabled("asset_module")) {
      return Promise.resolve();
    }
    const subjectModelsSchema: Array<SubjectModelSchema> = subjectModels.map((subjectModel) => {
      const result: SubjectModelSchema = new SubjectModelSchema(
        subjectModel,
        this.tenantId,
        this.extractStructImagePathFromSAS(subjectModel, this.tenantId)
      );
      return result;
    });
    return await Promise.all([this.subjectModelDbService.putAll(subjectModelsSchema).toPromise(), this.saveInspectionStructureImage(subjectModels)])
      .then(() => true)
      .catch((e) => {
        console.log(e);
        return false;
      });
  }

  private async saveSubjectModelLocations(subjectModelLocations: SubjectModelLocations[]) {
    const subjectModelLocationsSchema = subjectModelLocations.map(
      (sml) => new SubjectModelLocationsSchema(sml.subjectModelId, sml.version, sml.locations, sml.isNewestVersion)
    );
    await this.subjectModelLocationsDbService.putAll(subjectModelLocationsSchema).toPromise().then();
  }

  private async saveSubjectAttributes(subjectAttributes: SubjectAttributes[]) {
    const subjectAttributesSchema = subjectAttributes.map(
      (sa) =>
        new SubjectAttributesSchema(sa.subjectId, sa.version, sa.owner, sa.country, sa.state, sa.district, sa.site, sa.project, sa.isNewestVersion)
    );
    await this.subjectAttributesDbService.putAll(subjectAttributesSchema).toPromise().then();
  }

  private async saveUser(users: UserDetail[]) {
    const userSchema = users.map((user) => new UserSchema(this.tenantId, user));
    await this.userDbService.putAll(userSchema).toPromise().then();
  }

  private async saveTitle(title: Title) {
    const titleSchema = new TitleSchema(this.tenantId, title);
    await this.titleDbService.putAll(titleSchema).toPromise().then();
  }

  private async saveTags(tags: Tags) {
    const tagsSchema = new TagsSchema(this.tenantId, tags);
    await this.tagsDbService.putAll(tagsSchema).toPromise().then();
  }

  private async saveMark(mark: Mark) {
    const markSchema = new MarkSchema(this.tenantId, mark);
    await this.markDbService.putAll(markSchema).toPromise().then();
  }

  private async saveTenantConfig(configs: ConfigurationItem[]) {
    const configurationItemSchema = configs.map((config) => new TenantConfigSchema(this.tenantId, config));
    await this.tenantConfigDbService.putAll(configurationItemSchema).toPromise().then();
  }

  private async saveIndividualReportTemplate(reportTemplates: IndividualReportTemplate[]) {
    const individualReportTemplateSchemas = reportTemplates.map(
      (reportTemplate) => new IndividualReportTemplateSchema(reportTemplate, this.tenantId)
    );
    const obs = reportTemplates.map((template) => this.saveBase64TemplateImage(template.imagePathList));

    await Promise.all([this.individualReportTemplateDbService.putAll(individualReportTemplateSchemas).toPromise(), zip(...obs).toPromise()]).then();
  }

  private async saveInspectionStructureImage(subjectModels: SubjectModel[]) {
    const getStructureImageFile = async (model: SubjectModel) => {
      if (!model.structImagePath) {
        this.progressDialogService.updateProgressStep(DATA_SYNC_STEP.DOWNLOAD_SUBJECT_MODEL_IMAGE_FILE);
        return;
      }
      const fileName: string = this.getStructImageFileNameFromSAS(model, this.tenantId);
      const file = await this.utilsService.getFileFromUrl(model.structImagePath, fileName, null);
      const savePath: string = this.getSaveStructImageFilePathFromSAS(model, this.tenantId);
      await this.fileSystemAccessService.saveFile(file, savePath);
      this.progressDialogService.updateProgressStep(DATA_SYNC_STEP.DOWNLOAD_SUBJECT_MODEL_IMAGE_FILE);
    };

    await Promise.all(subjectModels.map(getStructureImageFile));
  }

  private saveBase64InspImages(
    inspectionImages: InspectionImage[],
    imgFiles: File[],
    modifiedImgFiles: File[],
    remeasuredImgFiles: File[],
    thumbnailImgFiles: File[],
    thumbnailModifiedImgFiles: File[],
    thumbnailRemeasuredImgFiles: File[],
    imageSchemas: InspectionImageSchema[]
  ) {
    const tasks: Promise<boolean>[] = [];

    imgFiles.forEach(async (file, i) => {
      const inspImg = inspectionImages[i];
      const modifiedImgFile = modifiedImgFiles[i];
      const remeasuredImgFile = remeasuredImgFiles[i];
      const thumbnailImgFile = thumbnailImgFiles[i];
      const thumbnailModifiedImgFile = thumbnailModifiedImgFiles[i];
      const thumbnailRemeasuredImgFile = thumbnailRemeasuredImgFiles[i];

      const localFilePath = imageSchemas.find((value) => value.id == inspImg.id).localFilePath;
      const thumbFilePath = imageSchemas.find((value) => value.id == inspImg.id).thumbFilePath;
      const saveImagePath = this.getSaveFilePathFromLocalFilePath(localFilePath);

      let task = firstValueFrom(this.uploadManagementDbService.get(this.tenantId, this.userId, inspImg.id, UploadDataType.InspectionImageFile)).then(
        (uploadRecord) => {
          // A pending file record means the local original carries changes (re-measurement
          // metadata embedded in its XMP) that were not pushed yet. Keep the local file and its
          // pending record instead of overwriting them with the older online copy.
          if (uploadRecord && uploadRecord.isUploaded === false) {
            return true;
          }
          return this.fileSystemAccessService.saveFile(file, saveImagePath).then((flag) => {
            return flag
              ? this.putUploadManageSchemaForInspImg(inspImg, UploadDataType.InspectionImageFile).then(() => of(flag).toPromise())
              : of(flag).toPromise();
          });
        }
      );

      const thumbFileName = thumbnailImgFile.name.split(".").pop();
      const saveThumbnailImagePath = this.getSaveFilePathFromLocalFilePath(thumbFilePath, null, thumbFileName);
      task = task
        .then(() => this.fileSystemAccessService.saveFile(thumbnailImgFile, saveThumbnailImagePath))
        .then((flag) => {
          return flag
            ? this.putUploadManageSchemaForInspImg(inspImg, UploadDataType.InspectionThumbnailImageFile).then(() => of(flag).toPromise())
            : of(flag).toPromise();
        });

      if (modifiedImgFile) {
        const saveAnnotationImagePath = this.getSaveFilePathFromLocalFilePath(localFilePath, "modified");
        task = task
          .then(() => this.fileSystemAccessService.saveFile(modifiedImgFile, saveAnnotationImagePath))
          .then((flag) => {
            return flag
              ? this.putUploadManageSchemaForInspImg(inspImg, UploadDataType.InspectionModifiedImageFile).then(() => of(flag).toPromise())
              : of(flag).toPromise();
          });
      }

      if (remeasuredImgFile) {
        const saveRemeasuredImagePath = this.getSaveFilePathFromLocalFilePath(localFilePath, "measurement");
        task = task
          .then(() => this.fileSystemAccessService.saveFile(remeasuredImgFile, saveRemeasuredImagePath))
          .then((flag) => {
            return flag
              ? this.putUploadManageSchemaForInspImg(inspImg, UploadDataType.InspectionMeasurementImageFile).then(() => of(flag).toPromise())
              : of(flag).toPromise();
          });
      }

      if (thumbnailModifiedImgFile) {
        const annotationThumbFileName = thumbnailModifiedImgFile.name.split(".").pop();
        const saveThumbnailAnnotationImagePath = this.getSaveFilePathFromLocalFilePath(thumbFilePath, "modified", annotationThumbFileName);
        task = task
          .then(() => this.fileSystemAccessService.saveFile(thumbnailModifiedImgFile, saveThumbnailAnnotationImagePath))
          .then((flag) => {
            return flag
              ? this.putUploadManageSchemaForInspImg(inspImg, UploadDataType.InspectionModifiedThumbnailImageFile).then(() => of(flag).toPromise())
              : of(flag).toPromise();
          });
      }

      if (thumbnailRemeasuredImgFile) {
        const remeasuredThumbFileName = thumbnailRemeasuredImgFile.name.split(".").pop();
        const saveThumbnailRemeasuredImagePath = this.getSaveFilePathFromLocalFilePath(thumbFilePath, "measurement", remeasuredThumbFileName);
        task = task
          .then(() => this.fileSystemAccessService.saveFile(thumbnailRemeasuredImgFile, saveThumbnailRemeasuredImagePath))
          .then((flag) => {
            return flag
              ? this.putUploadManageSchemaForInspImg(inspImg, UploadDataType.InspectionMeasurementThumbnailImageFile).then(() => of(flag).toPromise())
              : of(flag).toPromise();
          });
      }

      tasks.push(task);
    });

    return Promise.all(tasks)
      .then(() => {
        return true;
      })
      .catch((error) => {
        console.log(error);
        return false;
      });
  }

  private getSaveFilePathFromLocalFilePath(localFilePath: string, imageMode: string = null, extension: string = null) {
    if (!localFilePath) return null;
    const pathSeparator = this.fileSystemAccessService.getSystemPathSeparator();
    if (extension) {
      const localFileName = localFilePath.split(".").pop();
      localFilePath = localFilePath.replace("/files/", "/thumbs/").replace(localFileName, extension);
    }

    if (!imageMode) {
      return localFilePath.replace(/\//g, pathSeparator);
    } else {
      const fileName = localFilePath.split("/").slice(-1)[0];
      const filePathParts = localFilePath.split("/");
      filePathParts.splice(filePathParts.length - 1, 0, imageMode);
      localFilePath = filePathParts.join(pathSeparator);
      return localFilePath.replace(fileName, `${imageMode}_${fileName}`);
    }
  }

  private async getImageFiles(inspectionImages: InspectionImage[]) {
    if (!inspectionImages || inspectionImages.length < 1) {
      return of([[] as File[], [] as File[]]).toPromise();
    }

    const term = "inspections";

    const getImagePromise: Promise<File>[] = [];
    const getAnnotationImagePromise: Promise<File>[] = [];
    const getRemeasurementImagePromise: Promise<File>[] = [];
    const getThumbnailImagePromise: Promise<File>[] = [];
    const getAnnotationThumbnailImagePromise: Promise<File>[] = [];
    const getRemeasurementThumbnailImagePromise: Promise<File>[] = [];
    inspectionImages.forEach((img) => {
      // Original Image
      const fileName = this.getFileNameFromSAS(img.originalFilePath, term);
      const imagePromise = this.utilsService.getFileFromUrl(img.originalFilePath, fileName);

      const modifiedFileName = this.getFileNameFromSAS(img.modifiedFilePath, term);
      const annotationPromise = img.hasModifiedImage
        ? this.utilsService.getFileFromUrl(img.modifiedFilePath, modifiedFileName)
        : of(null as File).toPromise();

      const remeasuredFileName = this.getFileNameFromSAS(img.measurementFilePath, term);
      const remeasurementPromise = img.hasMeasurementImage
        ? this.utilsService.getFileFromUrl(img.measurementFilePath, remeasuredFileName)
        : of(null as File).toPromise();

      // Thumbnail Image
      const thumbnailFileName = this.getFileNameFromSAS(img.originalThumbFilePath, term);
      const thumbnailPromise = this.utilsService.getFileFromUrl(img.originalThumbFilePath, thumbnailFileName);

      const modifiedThumbnailFileName = this.getThumbPathFromLocalFilePath(this.getFileNameFromSAS(img.modifiedThumbFilePath, term));
      const annotationThumbnailPromise = img.hasModifiedImage
        ? this.utilsService.getFileFromUrl(img.modifiedThumbFilePath, modifiedThumbnailFileName)
        : of(null as File).toPromise();

      const remeasuredThumbnailFileName = this.getThumbPathFromLocalFilePath(this.getFileNameFromSAS(img.measurementThumbFilePath, term));
      const remeasurementThumbnailPromise = img.hasMeasurementImage
        ? this.utilsService.getFileFromUrl(img.measurementThumbFilePath, remeasuredThumbnailFileName)
        : of(null as File).toPromise();

      getImagePromise.push(imagePromise);
      getAnnotationImagePromise.push(annotationPromise);
      getRemeasurementImagePromise.push(remeasurementPromise);
      getThumbnailImagePromise.push(thumbnailPromise);
      getAnnotationThumbnailImagePromise.push(annotationThumbnailPromise);
      getRemeasurementThumbnailImagePromise.push(remeasurementThumbnailPromise);
    });

    return await Promise.all([
      Promise.all(getImagePromise),
      Promise.all(getAnnotationImagePromise),
      Promise.all(getRemeasurementImagePromise),
      Promise.all(getThumbnailImagePromise),
      Promise.all(getAnnotationThumbnailImagePromise),
      Promise.all(getRemeasurementThumbnailImagePromise)
    ]);
  }

  private getThumbPathFromLocalFilePath(localFilePath: string): string {
    return localFilePath.replace("/files/", "/thumbs/");
  }

  private async saveBase64TemplateImage(imagePathList: ImagePath[]) {
    const term = "reportTemplates";
    const getImagePromise = imagePathList.map((path) => {
      const fileName = this.getFileNameFromSAS(path.src, term);

      return this.utilsService.getFileFromUrl(path.src, fileName, null, () =>
        this.progressDialogService.updateProgressStep(DATA_SYNC_STEP.DOWNLOAD_REPORT_TEMPLATE_IMAGE_FILE)
      );
    });

    return await Promise.all(getImagePromise)
      .then(async (images) => {
        const tasks = images.map((file, i) => {
          const savePath = this.getSaveFilePathFromSAS(imagePathList[i].src, term);
          return this.fileSystemAccessService.saveFile(file, savePath);
        });

        return Promise.all(tasks);
      })
      .then((results) => results.every((_) => _))
      .catch((e) => {
        console.log(e);
        return false;
      });
  }

  private async saveCurrentUser(user: User) {
    const userSchema = new CurrentUserSchema(user);
    await this.currentUserDbService.put(userSchema).toPromise().then();
  }

  private async saveTenantDetail(tenantDetail: TenantDetail) {
    const tenantDetailSchema = new TenantDetailSchema(tenantDetail.id, tenantDetail.name);
    await this.tenantDetailDbService.put(tenantDetailSchema).toPromise().then();
  }

  private async saveConnectorStatus(connectorStatus: ConnectorStatus) {
    const connectorStatusSchema = new ConnectorSchema(connectorStatus);
    await this.connectorDbService.updateConnectorStatus(connectorStatusSchema).toPromise();
  }

  private extractPathFromSAS(sasUrl: string, term: string) {
    if (!sasUrl) {
      return "";
    }
    const indexOfTerm = sasUrl.indexOf(term);
    const temp = indexOfTerm > 0 ? sasUrl.slice(indexOfTerm) : sasUrl;

    // '?'より後ろを削除
    const indexOfQ = temp.indexOf("?");
    const result = indexOfQ > 0 ? temp.slice(0, indexOfQ) : temp;

    return decodeURIComponent(result);
  }

  /**
   * 検査テンプレートの structImagePath の URL 文字列から、ファイル操作に必要な文字列を抽出して返す。
   * Backend の構成に合わせるため、検査テンプレートの画像ファイルはテナント ID を除いたディレクトリー構造を抽出し、
   * 削除済み検査テンプレートの場合は、「temp/deleted/～」以下となるように文字列を抽出する。
   *
   * @param subjectModel SubjectModel。
   * @param tenantId テナント ID。
   * @returns ファイル操作に必要な文字列。
   */
  private extractStructImagePathFromSAS(subjectModel: SubjectModel, tenantId: string): string {
    const sasUrl: string = subjectModel.structImagePath;
    if (!sasUrl) {
      return "";
    }

    const regExp: RegExp = new RegExp(tenantId + "\\/", "g");
    let indexOfTerm: number = -1;
    if (regExp.test(sasUrl)) {
      indexOfTerm = regExp.lastIndex;
    }
    const temp: string = indexOfTerm > -1 ? sasUrl.slice(indexOfTerm) : sasUrl;

    // '?'より後ろを削除
    const indexOfQ: number = temp.indexOf("?");
    const result: string = indexOfQ > 0 ? temp.slice(0, indexOfQ) : temp;

    return decodeURIComponent(result);
  }

  private getFileNameFromSAS(sasUrl: string, term: string) {
    const path = this.extractPathFromSAS(sasUrl, term);
    const parts = path.split("\\");
    const fileName = parts[parts.length - 1];
    return fileName;
  }

  /**
   * 検査テンプレートのファイル名を取得する。
   *
   * @param subjectModel SubjectModel。
   * @param tenantId テナント ID。
   * @returns 検査テンプレートのファイル名。
   */
  private getStructImageFileNameFromSAS(subjectModel: SubjectModel, tenantId: string): string {
    const path: string = this.extractStructImagePathFromSAS(subjectModel, tenantId);
    const fileName: string = path.split(/\\|\//).pop();
    return fileName;
  }

  private getSaveFilePathFromSAS(sasUrl: string, term: string) {
    const path = this.extractPathFromSAS(sasUrl, term);
    return path.replace(/\//g, "\\");
  }

  /**
   * 検査テンプレートの structImagePath を Windows ローカルファイルに適したパスに変換して返す。
   * 検査テンプレートの structImagePath を DB に書き込む場合は extractStructImagePathFromSAS() を使用する。
   *
   * @param subjectModel SubjectModel。
   * @param tenantId テナント ID。
   * @returns Windows ローカルに適したファイルパス。
   */
  private getSaveStructImageFilePathFromSAS(subjectModel: SubjectModel, tenantId: string): string {
    const path: string = this.extractStructImagePathFromSAS(subjectModel, tenantId);
    return path.replace(/\//g, "\\");
  }

  private putUploadManageSchemaForInspImg(inspImg: InspectionImage, dataType: UploadDataType) {
    const ums = new UploadManagementSchema(inspImg.id, this.tenantId, dataType, false, this.userId, inspImg.inspectionId);
    return this.uploadManagementDbService.put(ums).toPromise();
  }

  private async genInspectionImageSchema(
    inspectionImage: InspectionImage,
    annotation: AnnotationImageDetail,
    tenantId: string,
    localFilePath: string,
    imageMetadataForOffline: ImageMetadataForOffline
  ) {
    const result = new InspectionImageSchema();
    result.setInspectionImageDetail(inspectionImage, annotation, tenantId, this.userId);
    result.localFilePath = localFilePath;

    switch (imageMetadataForOffline.imageType) {
      case MEASUREMENT_IMAGE_TYPE.VEGA_IMAGE:
        result.metadata = JSON.stringify(imageMetadataForOffline.vegaImageMetadata);
        result.vegaMeasurement = imageMetadataForOffline.vegaMeasurement;
        break;
      case MEASUREMENT_IMAGE_TYPE.IV7_IMAGE:
        result.metadata = JSON.stringify(imageMetadataForOffline.iv7ImageMetadata);
        break;
      case MEASUREMENT_IMAGE_TYPE.SOLEIL_IMAGE:
        result.metadata = JSON.stringify(imageMetadataForOffline.soleilImageMetadata);
        break;
      case MEASUREMENT_IMAGE_TYPE.BRIAN_IMAGE:
        result.metadata = JSON.stringify(imageMetadataForOffline.brianImageMetadata);
        break;
      case MEASUREMENT_IMAGE_TYPE.THESEUS_IMAGE:
        result.metadata = JSON.stringify(imageMetadataForOffline.theseusImageMetadata);
        break;
      case MEASUREMENT_IMAGE_TYPE.ANYIMAGE_IMAGE:
        result.metadata = JSON.stringify(imageMetadataForOffline.anyImageMetadata);
        break;
      case MEASUREMENT_IMAGE_TYPE.VIDEO_IMAGE:
        result.metadata = JSON.stringify(imageMetadataForOffline.videoMetadata);
        break;
    }

    return result;
  }

  private genLocationFolder(location: SubjectLocation, folderList: string[]) {
    if (location.children.length > 0) {
      location.children.forEach((child) => {
        this.genLocationFolder(child, folderList);
      });
    } else {
      if (location.isDataPlate) {
        const folderName = "00_" + location.identifier;
        folderList.push(folderName);
      } else {
        const folderName = String(this.folderIndex).padStart(2, "0") + "_" + location.identifier;
        folderList.push(folderName);
        this.folderIndex++;
      }
    }

    return folderList;
  }

  private genLocalFilePath(inspectionTitle: string, folderName: string, fileName: string) {
    return "inspections/".concat(inspectionTitle, "/files/", folderName, "/", fileName);
  }

  private saveReports(reports: InspectionReport[], reportItems: InspectionReportListItem[]) {
    if (reports.length < 1) {
      return of([] as boolean[][]);
    }

    const reportSchemas = reports.map((report) => {
      const reportSchema = new IndividualReportSchema();
      // 同一レポート内の最新版かどうか
      const isNewestVersion = !reports.filter((r) => r.reportId === report.reportId).some((r) => report.version < r.version);
      const reportItem = reportItems.find((item) => item.reportId === report.reportId);
      reportSchema.setInspectionReport(report, this.tenantId, isNewestVersion, this.userId, reportItem.thumbFilePath);
      return reportSchema;
    });

    const reportSaveOb = this.individualReportDbService.putALL(reportSchemas).pipe(
      switchMap((schemas) => {
        // statusがCompleted以外の検査のみアップロード対象として管理する
        const umsObs = schemas
          .filter((report) => report.status !== INSPECTION_REPORT_STATUS_CODE.COMPLETED)
          .map(
            (reportSchema) =>
              new UploadManagementSchema(
                reportSchema.id,
                this.tenantId,
                UploadDataType.IndividualReport,
                false,
                this.userId,
                reportSchema.inspectionId
              )
          );
        return this.uploadManagementDbService.putAll(umsObs);
      })
    );

    const reportWithPdfList = reports.filter((report) => report.status === INSPECTION_REPORT_STATUS_CODE.COMPLETED);
    let pdfObZip = of([] as Blob[]);
    if (reportWithPdfList.length > 0) {
      const pdfObs = reportWithPdfList.map((report) => this.reportOnlineService.getInspectionReportPdf(report.reportId, report.version));
      pdfObZip = zip(...pdfObs);
    }

    const getThumbnails = reports.map((report) => {
      const target = reportItems.find((item) => item.reportId === report.reportId);
      return target && target.thumbFilePath !== null && target.thumbFilePath !== ""
        ? this.utilsService.getFileFromUrl(target.thumbFilePath, `${report.title}-${report.version}.png`, "image/png")
        : of(null as Blob).toPromise();
    });
    const thumbnails$ = from(Promise.all(getThumbnails));

    return zip(reportSaveOb, pdfObZip, thumbnails$).pipe(
      switchMap((values) => {
        const pdfList = values[1];
        const thumbnailList = values[2];

        let savePdfPromiseList: Promise<boolean>[] = [];
        if (pdfList.length > 0) {
          savePdfPromiseList = pdfList.map((pdf, index) => {
            const report = reportWithPdfList[index];
            const fileName = `report-${report.version}.pdf`;
            const file = new File([pdf], fileName, { type: pdf.type });
            const savePath = this.genReportContentsPath(report.inspectionIds[0], report.reportId, fileName);
            return this.fileSystemAccessService.saveFile(file, savePath);
          });
        }

        const saveThumbPromiseList: Promise<boolean>[] = [];
        if (thumbnailList.length > 0) {
          savePdfPromiseList = thumbnailList.map((thumb, index) => {
            if (!thumb) {
              return of(true).toPromise();
            }

            const report = reports[index];
            const fileName = `thumbnail-${report.version}.png`;
            const file = new File([thumb], fileName, { type: thumb.type });
            const savePath = this.genReportContentsPath(report.inspectionIds[0], report.reportId, fileName);
            return this.fileSystemAccessService.saveFile(file, savePath);
          });
        }
        return zip(from(Promise.all(savePdfPromiseList)), from(Promise.all(saveThumbPromiseList)));
      })
    );
  }
  private genReportContentsPath(inspectionId: string, reportId: string, fileName: string) {
    const pathSeparator = this.fileSystemAccessService.getSystemPathSeparator();
    return `inspections${pathSeparator}${inspectionId}${pathSeparator}reports${pathSeparator}${reportId}${pathSeparator}${fileName}`;
  }

  /**
   * Open progress bar dialog.
   * @param syncStepCount
   * Number of progress items in the Progress bar
   * @returns
   */
  private openProgressDialog(syncStepCount: number): MatDialogRef<ProgressDialogComponent> {
    this.oscSpinnerService.hide(true);
    return this.dialog.open(ProgressDialogComponent, {
      width: "600px",
      maxHeight: "700px",
      minHeight: "385px",
      disableClose: true,
      data: {
        syncStepCount: syncStepCount
      }
    });
  }

  /**
   * IdentifierからLocationFileName({{ファイル番号}} + "_" + {{Identifier}})を取得
   * @param locations
   * @param locationIdentifier
   * @returns LocationFileName({{ファイル番号}} + "_" + {{Identifier}})
   */
  private getLocationFolder(locations: SubjectLocation[], locationIdentifier: string): string {
    for (const location of locations) {
      if (location.identifier === locationIdentifier) {
        return location.folderName;
      } else if (location.children.length > 0) {
        const childResult = this.getLocationFolder(location.children, locationIdentifier);
        if (childResult !== "Unallocated") {
          return childResult;
        }
      }
    }
    return "Unallocated";
  }
}
